<template>
  <article :class="$style.item">
    <div :class="$style.image">
      <img v-if="imageUrl" :src="imageUrl" :alt="reward.resource?.name" />
    </div>

    <div :class="$style.info">
      <p :class="$style.percent">
        {{ t('top', { percent: reward.top_percent }) }}
      </p>
      <p :class="$style.name">{{ rewardName }}</p>
    </div>
  </article>
</template>

<i18n>
{
  "ru": {
    "top": "в Топ {percent}%"
  }
}
</i18n>

<script setup lang="ts">
import type { FantasyReward } from '~/types/fantasy'
import formatNumber from '~/utils/number'

const props = defineProps<{
  reward: FantasyReward
}>()

const { t } = useI18n()

const imageUrl = computed(() => props.reward.resource?.image_url?.small)
const rewardName = computed(() => {
  const isCoin = props.reward.resource?.source?.id === 'coin'

  if (isCoin) {
    return `${formatNumber(props.reward.amount)} Liga Coins`
  }

  return props.reward.resource?.name || ''
})
</script>

<style lang="scss" module>
.item {
  flex: 1 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8em;
  padding: 1.2em;
  border-radius: 1.2em;
  background: color-get(--root-rgb-bg-card);
  overflow: hidden;

  @include down(md) {
    flex: 0 0 auto;
    max-width: min-content;
    min-width: 14.8em;
    height: 14.8em;
  }
}

.image {
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 10.2em;
  height: 8.4em;
  overflow: hidden;

  @include down(md) {
    width: 100%;
    height: auto;
    flex: 1 1;
    min-height: 0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.info {
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: center;

  @include down(md) {
    flex-shrink: 0;
    width: auto;
  }
}

.percent {
  @include typo-get(label-s);
  color: color-get(--root-rgb-text-2);
}

.name {
  @include typo-get(body-l);
  color: color-get(--root-rgb-text-1);

  @include up(md) {
    @include text-line-clamp(2);
  }

  @include down(md) {
    white-space: nowrap;
  }
}
</style>
